import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Label } from '@paulownia/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import { Plus, Trash2, Clock, MapPin, Calendar, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listMarkets,
  getTenantMarkets,
  linkTenantToMarket,
  unlinkTenantFromMarket,
  updateTenantMarket,
} from '@paulownia/shared/services/markets';
import type { Market, TenantMarket } from '@paulownia/shared/types';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';

interface Tenant {
  id: string;
  name: string;
}

interface TenantMarketWithDetails extends TenantMarket {
  market?: Market;
}

export const TenantMarketsAdmin = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [tenantMarkets, setTenantMarkets] = useState<TenantMarketWithDetails[]>([]);
  const [loading, setLoading] = useState(false);

  // Form for linking
  const [selectedMarketId, setSelectedMarketId] = useState<string>('');
  const [orderStartTime, setOrderStartTime] = useState<string>('');
  const [orderEndTime, setOrderEndTime] = useState<string>('');

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadTenantMarkets(selectedTenantId);
    } else {
      setTenantMarkets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [, marketsResult] = await Promise.all([
        loadTenants(),
        listMarkets(),
      ]);
      setMarkets(marketsResult);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    const { functions } = initFirebase();
    const listAllTenants = httpsCallable(functions, 'listAllTenants');
    const result = await listAllTenants();
    const data = result.data as { success: boolean; tenants: Tenant[] };

    if (data.success) {
      setTenants(data.tenants);
      return data.tenants;
    }
    return [];
  };

  const loadTenantMarkets = async (tenantId: string) => {
    setLoading(true);
    try {
      const associations = await getTenantMarkets(tenantId);

      // Enrich with market details
      const enrichedAssociations: TenantMarketWithDetails[] = associations.map((tm) => ({
        ...tm,
        market: markets.find((m) => m.id === tm.marketId),
      }));

      setTenantMarkets(enrichedAssociations);
    } catch (error) {
      console.error('Error loading tenant markets:', error);
      toast.error('Erreur lors du chargement des marchés du commerçant');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkMarket = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTenantId || !selectedMarketId) {
      toast.error('Veuillez sélectionner un commerçant et un marché');
      return;
    }

    // Check if already linked
    if (tenantMarkets.some((tm) => tm.marketId === selectedMarketId)) {
      toast.error('Ce marché est déjà associé à ce commerçant');
      return;
    }

    setLoading(true);
    try {
      await linkTenantToMarket(selectedTenantId, selectedMarketId, {
        orderStartTime: orderStartTime || undefined,
        orderEndTime: orderEndTime || undefined,
      });
      toast.success('Marché associé au commerçant');
      setSelectedMarketId('');
      setOrderStartTime('');
      setOrderEndTime('');
      await loadTenantMarkets(selectedTenantId);
    } catch (error) {
      console.error('Error linking market:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'association');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkMarket = async (tenantMarket: TenantMarketWithDetails) => {
    const marketName = tenantMarket.market
      ? `${tenantMarket.market.city} - ${DAY_OF_WEEK_LABELS[tenantMarket.market.dayOfWeek]}`
      : 'ce marché';

    if (!confirm(`Êtes-vous sûr de vouloir dissocier ${marketName} de ce commerçant ?`)) {
      return;
    }

    setLoading(true);
    try {
      await unlinkTenantFromMarket(tenantMarket.id);
      toast.success('Marché dissocié');
      await loadTenantMarkets(selectedTenantId);
    } catch (error) {
      console.error('Error unlinking market:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la dissociation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTimes = async (
    tenantMarket: TenantMarketWithDetails,
    startTime: string,
    endTime: string
  ) => {
    setLoading(true);
    try {
      await updateTenantMarket(tenantMarket.id, {
        orderStartTime: startTime || undefined,
        orderEndTime: endTime || undefined,
      });
      toast.success('Horaires mis à jour');
      await loadTenantMarkets(selectedTenantId);
    } catch (error) {
      console.error('Error updating times:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Available markets (not yet linked to selected tenant)
  const availableMarkets = markets.filter(
    (m) => m.isActive && !tenantMarkets.some((tm) => tm.marketId === m.id)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Association Commerçants / Marchés</h2>
      </div>

      {/* Tenant Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sélectionner un commerçant</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedTenantId}
            onValueChange={setSelectedTenantId}
            disabled={loading}
          >
            <SelectTrigger className="w-full md:w-96">
              <SelectValue placeholder="Choisir un commerçant..." />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedTenantId && (
        <>
          {/* Link Market Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Associer un marché
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLinkMarket} className="space-y-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="market">Marché</Label>
                    <Select
                      value={selectedMarketId}
                      onValueChange={setSelectedMarketId}
                      disabled={loading || availableMarkets.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un marché..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMarkets.map((market) => (
                          <SelectItem key={market.id} value={market.id}>
                            {market.city} - {DAY_OF_WEEK_LABELS[market.dayOfWeek]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {availableMarkets.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Tous les marchés sont déjà associés
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="orderStartTime">
                      Début commandes (optionnel)
                    </Label>
                    <Input
                      id="orderStartTime"
                      type="time"
                      value={orderStartTime}
                      onChange={(e) => setOrderStartTime(e.target.value)}
                      disabled={loading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Laisser vide pour utiliser l'heure du marché
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="orderEndTime">
                      Fin commandes (optionnel)
                    </Label>
                    <Input
                      id="orderEndTime"
                      type="time"
                      value={orderEndTime}
                      onChange={(e) => setOrderEndTime(e.target.value)}
                      disabled={loading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Laisser vide pour utiliser l'heure du marché
                    </p>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !selectedMarketId}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Associer le marché
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Linked Markets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Marchés du commerçant ({tenantMarkets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tenantMarkets.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Aucun marché associé à ce commerçant
                </p>
              ) : (
                <div className="space-y-4">
                  {tenantMarkets.map((tm) => (
                    <div
                      key={tm.id}
                      className="flex flex-col md:flex-row md:items-center gap-4 p-4 border rounded-lg"
                    >
                      {/* Market Info */}
                      <div className="flex-1">
                        {tm.market ? (
                          <>
                            <div className="font-medium flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {tm.market.city} - {DAY_OF_WEEK_LABELS[tm.market.dayOfWeek]}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                              <Clock className="h-3 w-3" />
                              Marché: {tm.market.openingTime} - {tm.market.closingTime}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                              <MapPin className="h-3 w-3" />
                              {tm.market.address}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            Marché non trouvé (ID: {tm.marketId})
                          </span>
                        )}
                      </div>

                      {/* Custom Order Times */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="space-y-1">
                          <Label className="text-xs">Début commandes</Label>
                          <Input
                            type="time"
                            className="w-28"
                            value={tm.orderStartTime || ''}
                            onChange={(e) => {
                              const newStartTime = e.target.value;
                              handleUpdateTimes(tm, newStartTime, tm.orderEndTime || '');
                            }}
                            disabled={loading}
                            placeholder={tm.market?.openingTime}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Fin commandes</Label>
                          <Input
                            type="time"
                            className="w-28"
                            value={tm.orderEndTime || ''}
                            onChange={(e) => {
                              const newEndTime = e.target.value;
                              handleUpdateTimes(tm, tm.orderStartTime || '', newEndTime);
                            }}
                            disabled={loading}
                            placeholder={tm.market?.closingTime}
                          />
                        </div>
                      </div>

                      {/* Actions */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleUnlinkMarket(tm)}
                        disabled={loading}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
